import { useState, useEffect, useCallback } from 'react';
import declaracionesJuradasService from '../services/declaracionesJuradasService';

/**
 * Hook para manejar Declaraciones Juradas (DJ1835, DJ1879, DJ1887, F1948)
 * Compatible con MCP
 *
 * @param {number} year - Año de la declaración
 * @param {number} id_company - ID de la empresa
 * @param {string} token - Token de autenticación
 */
const useDeclaracionesJuradas = (year, id_company, token) => {
    const [declaracionesJuradas, setDeclaracionesJuradas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchDeclaracionesJuradas = useCallback(async () => {
        if (!year || !id_company) return;

        setLoading(true);
        setError(null);

        try {
            const data = await declaracionesJuradasService.getDeclaracionesJuradas(
                year,
                id_company,
                token
            );
            setDeclaracionesJuradas(data);
        } catch (err) {
            setError(err.message || 'Error al cargar declaraciones juradas');
            console.error('Error fetching declaraciones juradas:', err);
        } finally {
            setLoading(false);
        }
    }, [year, id_company, token]);

    const createDeclaracionJurada = useCallback(async (formData) => {
        setLoading(true);
        setError(null);

        try {
            const result = await declaracionesJuradasService.createDeclaracionJurada(
                { ...formData, year, id_company },
                token
            );
            await fetchDeclaracionesJuradas();
            return result;
        } catch (err) {
            setError(err.message || 'Error al crear declaración jurada');
            console.error('Error creating declaracion jurada:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [year, id_company, token, fetchDeclaracionesJuradas]);

    const updateDeclaracionJurada = useCallback(async (id, formData) => {
        setLoading(true);
        setError(null);

        try {
            const result = await declaracionesJuradasService.updateDeclaracionJurada(
                id,
                { ...formData, id_company },
                token
            );
            await fetchDeclaracionesJuradas();
            return result;
        } catch (err) {
            setError(err.message || 'Error al actualizar declaración jurada');
            console.error('Error updating declaracion jurada:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [id_company, token, fetchDeclaracionesJuradas]);

    const deleteDeclaracionJurada = useCallback(async (id) => {
        setLoading(true);
        setError(null);

        try {
            await declaracionesJuradasService.deleteDeclaracionJurada(id, id_company, token);
            await fetchDeclaracionesJuradas();
        } catch (err) {
            setError(err.message || 'Error al eliminar declaración jurada');
            console.error('Error deleting declaracion jurada:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [id_company, token, fetchDeclaracionesJuradas]);

    useEffect(() => {
        if (year && id_company && token) {
            fetchDeclaracionesJuradas();
        }
    }, [year, id_company, token, fetchDeclaracionesJuradas]);

    return {
        declaracionesJuradas,
        loading,
        error,
        fetchDeclaracionesJuradas,
        createDeclaracionJurada,
        updateDeclaracionJurada,
        deleteDeclaracionJurada,
    };
};

export default useDeclaracionesJuradas;
